import {
  Card,
  CardActions,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { useFormik } from 'formik';
import { ReactElement, useCallback } from 'react';
import AddressAutoComplete from 'system/components/shared/search-address';
import * as yup from 'yup';
import PmiTextField from 'shell/components/shared/text-field';
import { MunicipalityVM } from 'system/view-models/municipality';
import TownHallAutoComplete from 'system/components/shared/search-town-hall';
import { regexOnlyNumber, regexEmail } from 'shell/constants/regex';
import { CirculationAreaClient } from 'shell/view-models/circulation-area-client';

export interface ServiceForm {
  code?: string;
  name?: string;
  shortDescription?: string;
  streetNumber?: string;
  area?: CirculationAreaClient | null;
  phoneNumber?: string;
  type?: string;
  civicRepresentative?: string;
  contextLastUpdate?: string;
  zoneCode?: string;
  zone?: MunicipalityVM | null;
  areaCircolazione?: number;
  managementCash?: boolean;
  pec?: string;
  peo?: string;
}

const validationSchema = yup.object({
  code: yup.string().required('Il campo codice è obbligatorio'),
  name: yup.string().required('Il campo nome è obbligatorio'),
  streetNumber: yup
    .string()
    .matches(regexOnlyNumber, 'Il campo deve essere un numero')
    .notRequired()
    .nullable()
    .typeError('Il numero civico deve essere un numero'),
  pec: yup.string().nullable().matches(regexEmail, 'Il campo deve rispettare il formato email'),
  peo: yup.string().nullable().matches(regexEmail, 'Il campo deve rispettare il formato email'),
  phoneNumber: yup
    .string()
    .matches(
      /^[0-9.\/+\(\)-]+$/gi,
      ' sono ammesi numeri e caratteri ".", " ", "/", "+", "(", ")" e "-"'
    ),
  shortDescription: yup.string().required('Il campo descrizione breve è obbligatorio'),
});

interface FormServiceProps {
  onOk: (form?: ServiceForm) => void;
  onCancel: (form?: ServiceForm) => void;
  title: string;
  children?: ReactElement | Array<ReactElement>;
  disabled?: boolean;
  initialValues?: ServiceForm;
  mode?: 'INSERT' | 'EDIT' | 'DETAIL';
}

const FormService = ({
  onOk,
  onCancel,
  title,
  children,
  disabled = false,
  initialValues,
  mode = 'INSERT',
}: FormServiceProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const serviceForm = useFormik({
    initialValues: initialValues || {
      code: '',
      name: '',
      shortDescription: '',
      phoneNumber: '',
      streetNumber: '',
      area: null,
      type: '',
      civicRepresentative: '',
      contextLastUpdate: '',
      zone: null,
      managementCash: false,
      pec: '',
      peo: '',
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const isEditMode = mode === 'EDIT';

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={serviceForm.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <PmiTextField
                  aria-label="CD Servizio"
                  label="CD Servizio *"
                  id="code"
                  name="code"
                  fullWidth
                  type="text"
                  variant="outlined"
                  inputProps={{
                    maxLength: '4',
                  }}
                  value={serviceForm.values.code}
                  disabled={disabled || isEditMode}
                  onChange={serviceForm.handleChange}
                  error={serviceForm.touched.code && Boolean(serviceForm.errors.code)}
                  helperText={serviceForm.errors.code}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <PmiTextField
                  label="Nome *"
                  id="name"
                  name="name"
                  fullWidth
                  inputProps={{
                    maxLength: '50',
                  }}
                  type="text"
                  variant="outlined"
                  value={serviceForm.values.name}
                  disabled={disabled}
                  onChange={serviceForm.handleChange}
                  error={serviceForm.touched.name && Boolean(serviceForm.errors.name)}
                  helperText={serviceForm.errors.name}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="management-cash"
                      checked={serviceForm.values.managementCash || false}
                      onChange={() =>
                        serviceForm.setFieldValue(
                          'managementCash',
                          !serviceForm.values.managementCash
                        )
                      }
                      inputProps={{ 'aria-label': 'gestione cassa' }}
                    />
                  }
                  label="Gestione cassa"
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <PmiTextField
                  label="Descrizione Breve *"
                  id="shortDescription"
                  name="shortDescription"
                  fullWidth
                  type="text"
                  inputProps={{
                    maxLength: '20',
                  }}
                  variant="outlined"
                  value={serviceForm.values.shortDescription}
                  disabled={disabled}
                  onChange={serviceForm.handleChange}
                  error={
                    serviceForm.touched.shortDescription &&
                    Boolean(serviceForm.errors.shortDescription)
                  }
                  helperText={serviceForm.errors.shortDescription}
                />
              </Grid>
              <Grid container spacing={2} item xs={12}>
                <Grid item xs={12} md={4}>
                  <TownHallAutoComplete
                    selectedValue={serviceForm.values.zone || { description: '' }}
                    handleChange={(element) => {
                      serviceForm.setFieldValue('zone', element || null);
                    }}
                    disabled={disabled}
                    label="Zona"
                    id="zone"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <AddressAutoComplete
                    selectedValue={serviceForm.values.area || null}
                    handleChange={(value) => {
                      serviceForm.setFieldValue('area', value);
                    }}
                    disabled={disabled}
                    id="area"
                    label="Area"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <PmiTextField
                    label="Numero civico"
                    id="streetNumber"
                    name="streetNumber"
                    fullWidth
                    inputProps={{
                      maxLength: '5',
                    }}
                    type="text"
                    variant="outlined"
                    disabled={disabled}
                    value={serviceForm.values.streetNumber}
                    onChange={serviceForm.handleChange}
                    error={
                      serviceForm.touched.streetNumber && Boolean(serviceForm.errors.streetNumber)
                    }
                    helperText={serviceForm.errors.streetNumber}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <PmiTextField
                    label="Esponente"
                    id="civicRepresentative"
                    name="civicRepresentative"
                    fullWidth
                    inputProps={{
                      maxLength: '3',
                    }}
                    variant="outlined"
                    value={serviceForm.values.civicRepresentative}
                    onChange={serviceForm.handleChange}
                    disabled={disabled}
                    error={
                      serviceForm.touched.civicRepresentative &&
                      Boolean(serviceForm.errors.civicRepresentative)
                    }
                    helperText={serviceForm.errors.civicRepresentative}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  label="PEC"
                  id="pec"
                  name="pec"
                  fullWidth
                  type="text"
                  variant="outlined"
                  disabled={disabled}
                  value={serviceForm.values.pec}
                  onChange={serviceForm.handleChange}
                  error={serviceForm.touched.pec && Boolean(serviceForm.errors.pec)}
                  helperText={serviceForm.errors.pec}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  label="PEO"
                  id="peo"
                  name="peo"
                  fullWidth
                  type="text"
                  variant="outlined"
                  disabled={disabled}
                  value={serviceForm.values.peo}
                  onChange={serviceForm.handleChange}
                  error={serviceForm.touched.peo && Boolean(serviceForm.errors.peo)}
                  helperText={serviceForm.errors.peo}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  label="Telefono"
                  id="phoneNumber"
                  name="phoneNumber"
                  fullWidth
                  type="text"
                  inputProps={{
                    maxLength: '12',
                  }}
                  variant="outlined"
                  disabled={disabled}
                  value={serviceForm.values.phoneNumber}
                  onChange={serviceForm.handleChange}
                  error={serviceForm.touched.phoneNumber && Boolean(serviceForm.errors.phoneNumber)}
                  helperText={serviceForm.errors.phoneNumber}
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <PmiTextField
                  label="Contesto ultimo aggiornamento"
                  id="contextLastUpdate"
                  name="contextLastUpdate"
                  fullWidth
                  inputProps={{
                    maxLength: '50',
                  }}
                  variant="outlined"
                  value={serviceForm.values.contextLastUpdate}
                  onChange={serviceForm.handleChange}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12}>
                {children}
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(serviceForm.values);
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormService.displayName = 'Form Service component';

export default FormService;
